import { useContext, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Chip,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material';
import { Refresh, SwapHoriz } from '@mui/icons-material';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../../../sporttia/all';
import SttCachedTable from '../../../sporttia/SttCachedTable';
import { SttTopFilteringControls } from '../../../sporttia/SttTopFilteringControls';
import { SPORTCENTER_ACTIVITIES_RELOCATIONS } from '../../../lib/queryKeys';
import {
    formatPriceByLocale,
    fullName,
    getErrorMessage,
    parseToDate,
    safeFormatDate,
} from '../../../lib/utils';
import { getDateLocale } from '../../../lib/dateLocale';
import { translatePlaceholders } from '../../../lib/translatePlaceholders';
import { useSingleFlight } from '../../../lib/useSingleFlight';
import useActivityRelocationsService from '../../../services/ActivityRelocationsService';
import type {
    ActivityRelocation,
    GetActivityRelocations200,
    RelocationRate,
    RelocationStatus,
    RelocationStatusFilter,
    ResendRelocation,
} from '../../../types/api/paths/ActivityRelocation';

type CopyRate = {
    id: number;
    name: string;
    enrollment?: number;
    trash?: boolean;
};

type Props = {
    item: { id?: number; ini?: string; rates?: CopyRate[] };
};

const POLL_INTERVAL_MS = 5000;

const IN_PROGRESS_STATUSES: RelocationStatus[] = ['PENDING', 'PROCESSING'];

const isInProgress = (relocation: ActivityRelocation) =>
    IN_PROGRESS_STATUSES.includes(relocation.status);

/**
 * Polls only while a row on screen is still being processed, so an idle tab
 * never queries the relocations view.
 */
export function relocationsPollInterval(
    data: GetActivityRelocations200 | undefined,
): number | false {
    return data?.rows?.some(isInProgress) ? POLL_INTERVAL_MS : false;
}

/** Product decision (CU-869ezdgnr): once the copy has started, relocating stops. */
const hasStarted = (ini?: string) => {
    const start = parseToDate(ini);
    return !!start && start <= new Date();
};

const STATUS_CHIP: Record<
    'DONE' | 'ERROR' | 'IN_PROGRESS',
    { surface: string; ink: string }
> = {
    DONE: { surface: 'success.light', ink: 'success.main' },
    ERROR: { surface: 'error.light', ink: 'error.main' },
    IN_PROGRESS: { surface: 'info.light', ink: 'info.main' },
};

/**
 * "Reubicaciones" tab of a copy duplicated relocating its students: the result
 * student by student, and a resend to another rate for what failed.
 */
export default function ActivityRelocations({
    item,
}: Props): React.ReactElement | null {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { getActivityRelocations, useResendActivityRelocations } =
        useActivityRelocationsService();
    const resendMutation = useResendActivityRelocations(item.id as number);
    const [status, setStatus] = useState<RelocationStatusFilter | undefined>();
    // Destination picked per failed relocation, by relocation id.
    const [chosenRates, setChosenRates] = useState<Record<number, number>>({});

    const refreshing =
        useIsFetching({ queryKey: [SPORTCENTER_ACTIVITIES_RELOCATIONS] }) > 0;

    const started = hasStarted(item.ini);
    const currency = cxt?.sc?.city?.country?.currency;
    const rateOptions = useMemo(
        () => (item.rates ?? []).filter((rate) => !rate.trash),
        [item.rates],
    );
    const queryOptions = useMemo(
        () => ({
            refetchInterval: (query: {
                state: { data?: GetActivityRelocations200 };
            }) => relocationsPollInterval(query.state.data),
        }),
        [],
    );

    const isResendable = (relocation: ActivityRelocation) =>
        !started &&
        relocation.status === 'ERROR' &&
        chosenRates[relocation.id] !== undefined;

    /** Shows the sent rows as in progress at once, so they cannot be picked again. */
    function markAsInProgress(ids: number[]) {
        queryClient.setQueriesData<GetActivityRelocations200>(
            { queryKey: [SPORTCENTER_ACTIVITIES_RELOCATIONS] },
            (old) =>
                old && {
                    ...old,
                    rows: old.rows.map((row) =>
                        ids.includes(row.id)
                            ? { ...row, status: 'PENDING', error: null }
                            : row,
                    ),
                },
        );
    }

    function forgetChosenRates(ids: number[]) {
        setChosenRates((current) =>
            Object.fromEntries(
                Object.entries(current).filter(
                    ([id]) => !ids.includes(Number(id)),
                ),
            ),
        );
    }

    const resend = useSingleFlight(async (rows: ActivityRelocation[]) => {
        const relocations: ResendRelocation[] = rows.flatMap((row) => {
            const groupId = chosenRates[row.id];

            return isResendable(row) && groupId !== undefined
                ? [{ id: row.id, groupId }]
                : [];
        });

        if (relocations.length === 0) {
            return;
        }

        const ids = relocations.map(({ id }) => id);
        markAsInProgress(ids);
        forgetChosenRates(ids);

        try {
            const { count } = await resendMutation.mutateAsync(relocations);
            cxt.showMessage('S', t('activity.relocation.resent', { count }));
        } catch (error) {
            cxt.showMessage('E', getErrorMessage(error));
        } finally {
            queryClient.invalidateQueries({
                queryKey: [SPORTCENTER_ACTIVITIES_RELOCATIONS],
            });
        }
    });

    /**
     * Only the list on screen: pages and filters seen before stay cached, and
     * refreshing them too multiplies the queries. A refresh in flight is not
     * restarted, and the button is disabled meanwhile.
     */
    function refresh() {
        queryClient.refetchQueries(
            { queryKey: [SPORTCENTER_ACTIVITIES_RELOCATIONS], type: 'active' },
            { cancelRefetch: false },
        );
    }

    function formatDate(date: string | null) {
        const parsed = parseToDate(date);
        const sameYear = parsed?.getFullYear() === new Date().getFullYear();

        return safeFormatDate(
            date,
            sameYear ? 'd MMM, HH:mm' : 'd MMM yyyy, HH:mm',
            {
                locale: getDateLocale(),
            },
        );
    }

    // El login separa a dos alumnos que se llaman igual.
    function renderStudent(relocation: ActivityRelocation) {
        return (
            <>
                {fullName(relocation.user)}
                {relocation.user?.login && (
                    <Typography
                        component="div"
                        variant="body2"
                        color="text.secondary"
                    >
                        @{relocation.user.login}
                    </Typography>
                )}
            </>
        );
    }

    function renderRate(rate: RelocationRate | null) {
        if (!rate) {
            return '—';
        }

        return (
            <Chip
                size="small"
                color="primary"
                label={`${rate.name} ${formatPriceByLocale(rate.price, currency)}`}
            />
        );
    }

    function renderDestination(relocation: ActivityRelocation) {
        if (started || relocation.status !== 'ERROR') {
            return renderRate(relocation.destinationRate);
        }

        return (
            <TextField
                select
                size="small"
                fullWidth
                value={chosenRates[relocation.id] ?? ''}
                onChange={(event) =>
                    setChosenRates({
                        ...chosenRates,
                        [relocation.id]: Number(event.target.value),
                    })
                }
                slotProps={{
                    select: {
                        displayEmpty: true,
                        renderValue: (value) =>
                            value === '' ? (
                                <Typography
                                    component="span"
                                    color="text.secondary"
                                >
                                    {t('activity.relocation.selectRate')}
                                </Typography>
                            ) : (
                                rateLabelOf(Number(value))
                            ),
                        // The column title names the field; the placeholder is only a prompt.
                        SelectDisplayProps: {
                            'aria-label': t(
                                'activity.relocation.destinationRate',
                            ),
                        } as React.HTMLAttributes<HTMLDivElement>,
                    },
                }}
            >
                {rateOptions.map((rate) => (
                    <MenuItem key={rate.id} value={rate.id}>
                        {rateLabelOf(rate.id)}
                    </MenuItem>
                ))}
            </TextField>
        );
    }

    function rateLabelOf(rateId: number) {
        const rate = rateOptions.find(({ id }) => id === rateId);

        return rate
            ? `${rate.name} ${formatPriceByLocale(rate.enrollment ?? 0, currency)}`
            : '';
    }

    function renderStatus(relocation: ActivityRelocation) {
        const key = isInProgress(relocation)
            ? 'IN_PROGRESS'
            : (relocation.status as 'DONE' | 'ERROR');
        const { surface, ink } = STATUS_CHIP[key];

        return (
            <Chip
                size="small"
                label={t(`activity.relocation.status.${key}`)}
                sx={{ bgcolor: surface, color: ink, fontWeight: 600 }}
            />
        );
    }

    if (!item?.id) {
        return null;
    }

    return (
        <>
            {started && (
                <Box mb={2}>
                    <Alert severity="info">
                        {t('activity.relocation.startedNotice')}
                    </Alert>
                </Box>
            )}
            <SttTopFilteringControls
                mb={2}
                fields={[
                    {
                        name: 'status',
                        type: 'select',
                        caption: t('Status'),
                        value: status ?? '',
                        options: [
                            { caption: t('All'), value: '' },
                            {
                                caption: t('activity.relocation.status.DONE'),
                                value: 'DONE',
                            },
                            {
                                caption: t('activity.relocation.status.ERROR'),
                                value: 'ERROR',
                            },
                            {
                                caption: t(
                                    'activity.relocation.status.IN_PROGRESS',
                                ),
                                value: 'EN_PROCESO',
                            },
                        ],
                    },
                ]}
                iconButtons={[
                    {
                        icon: <Refresh />,
                        caption: t('Refresh'),
                        onClick: refresh,
                        disabled: refreshing,
                    },
                ]}
                onFilter={({ status: selected }: { status?: string }) =>
                    setStatus(
                        (selected || undefined) as
                            | RelocationStatusFilter
                            | undefined,
                    )
                }
            />
            <SttCachedTable
                queryKey={SPORTCENTER_ACTIVITIES_RELOCATIONS}
                queryFn={({ activityId, ...params }: { activityId: number }) =>
                    getActivityRelocations(activityId, params)
                }
                queryParams={{ activityId: item.id, status }}
                queryOptions={queryOptions}
                prefetching={false}
                selection={
                    started
                        ? undefined
                        : {
                              isRowSelectable: isResendable,
                              actions: [
                                  {
                                      caption: t('activity.relocation.resend'),
                                      icon: <SwapHoriz />,
                                      onClick: (rows: ActivityRelocation[]) => {
                                          void resend(rows);
                                      },
                                  },
                              ],
                          }
                }
                columns={[
                    {
                        title: t('Student'),
                        align: 'left',
                        value: renderStudent,
                    },
                    {
                        title: t('activity.relocation.originRate'),
                        value: (row: ActivityRelocation) =>
                            renderRate(row.originRate),
                    },
                    {
                        title: t('activity.relocation.destinationRate'),
                        width: 240,
                        value: renderDestination,
                    },
                    {
                        title: t('Date'),
                        value: (row: ActivityRelocation) => (
                            <span className="whitespace-nowrap">
                                {formatDate(row.processedAt ?? row.createdAt)}
                            </span>
                        ),
                    },
                    {
                        title: t('activity.relocation.reason'),
                        align: 'left',
                        value: (row: ActivityRelocation) =>
                            row.error
                                ? translatePlaceholders(row.error.message, t)
                                : '—',
                    },
                    {
                        title: t('Status'),
                        value: renderStatus,
                    },
                ]}
            />
        </>
    );
}
